import React, { useState, useEffect, useRef } from 'react';
import {
    format,
    isSameDay,
    isAfter,
    isBefore,
    startOfMonth,
    endOfMonth,
    eachDayOfInterval,
    addMonths,
    subMonths,
    isToday,
    parse,
    isValid,
} from 'date-fns';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleLeft, faCircleRight } from '@fortawesome/free-regular-svg-icons';

const initDate = (startInput, endInput) => {
    if(endInput) {
        return endInput ;
    } else if (startInput) {
        return startInput;
    } else {
        return new Date();
    }
}

const DatePicker = ({ startDate, endDate, setStartDate, setEndDate }) => {
    const [startInput, setStartInput] = useState(startDate ? format(startDate, 'yyyy-MM-dd') : ''); // For controlled input value
    const [endInput, setEndInput] = useState(endDate ? format(endDate, 'yyyy-MM-dd') : ''); // For controlled input value
    const [currentDate, setCurrentDate] = useState(initDate(startInput, endInput)); // Use startDate to initialize month view if available
    const [showCalendar, setShowCalendar] = useState(false); // Control calendar visibility
    const [activeInput, setActiveInput] = useState(null); // Track which input is being edited ('start' or 'end')
    const containerRef = useRef(null);

    useEffect(() => {
        // Update the currentDate to the selected start date's month
        if (startDate) {
            setCurrentDate(initDate(startInput, endInput));
        }
    }, [startDate]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setShowCalendar(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleDateClick = (date) => {
        if (activeInput === 'start') {
            setStartDate(date);
            setStartInput(format(date, 'yyyy-MM-dd'));
            setShowCalendar(false);
        } else if (activeInput === 'end') {
            setEndDate(date);
            setEndInput(format(date, 'yyyy-MM-dd'));
            setShowCalendar(false);
        }
    };

    const handlePrevMonth = () => {
        setCurrentDate(subMonths(currentDate, 1));
    };

    const handleNextMonth = () => {
        setCurrentDate(addMonths(currentDate, 1));
    };

    const isInRange = (date) => {
        return startDate && endDate && isAfter(date, startDate) && isBefore(date, endDate);
    };

    const isSelected = (date) => {
        return (startDate && isSameDay(date, startDate)) || (endDate && isSameDay(date, endDate));
    };

    const handleInputChange = (e, isStart = true) => {
        const value = e.target.value;
        if (isStart) {
            setStartInput(value);
        } else {
            setEndInput(value);
        }
    };

    const handleInputBlur = (isStart = true) => {
        if (isStart) {
            const parsedDate = parse(startInput, 'yyyy-MM-dd', new Date());
            if (!isValid(parsedDate)) {
                // Reset to last valid selected date
                setStartInput(startDate ? format(startDate, 'yyyy-MM-dd') : '');
            } else {
                setStartDate(parsedDate);
                if (endDate && isAfter(parsedDate, endDate)) setEndDate(null);
                // Adjust the calendar to the month of the selected date
                setCurrentDate(startOfMonth(parsedDate));
            }
        } else {
            const parsedDate = parse(endInput, 'yyyy-MM-dd', new Date());
            if (!isValid(parsedDate) || (startDate && isBefore(parsedDate, startDate))) {
                // Reset to last valid selected date
                setEndInput(endDate ? format(endDate, 'yyyy-MM-dd') : '');
            } else {
                setEndDate(parsedDate);
                // Adjust the calendar to the month of the selected date
                setCurrentDate(startOfMonth(parsedDate));
            }
        }
    };

    const handleInputFocus = (isStart = true) => {
        setActiveInput(isStart ? 'start' : 'end');
        setShowCalendar(true);
        // Set calendar to the appropriate month
        const dateToShow = isStart
            ? (startDate || new Date())
            : (endDate || startDate || new Date());
        setCurrentDate(dateToShow);
    };

    const generateCalendar = () => {
        const startOfCurrentMonth = startOfMonth(currentDate);
        const endOfCurrentMonth = endOfMonth(currentDate);
        const days = eachDayOfInterval({ start: startOfCurrentMonth, end: endOfCurrentMonth });
        const startDay = startOfCurrentMonth.getDay(); // Day of the week the month starts on

        return (
            <div className="grid grid-cols-7 gap-1">
                {['M', 'T', 'O', 'T', 'F', 'L', 'S'].map((day) => (
                    <div key={Date.now().toString(36) + Math.random().toString(36).substring(2)} className="text-center font-normal text-sm text-mute-300">{day}</div>
                ))}
                {Array.from({ length: startDay }, (_, i) => <div key={i} />)}
                {days.map((date) => (
                    <div
                        key={date}
                        className={`p-2 text-center text-black text-sm font-normal cursor-pointer rounded-lg w-8 h-8 flex justify-center items-center ${isSelected(date)
                                ? 'bg-primary-300'
                                : isInRange(date)
                                    ? 'bg-primary-100'
                                    : isToday(date)
                                        ? 'border border-primary-300' // Highlight today's date
                                        : 'hover:bg-primary-100'
                            }`}
                        onClick={() => handleDateClick(date)}
                    >
                        {date.getDate()}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div ref={containerRef} className="w-72 text-black bg-white relative">
            <div className='flex flex-col'>
                <input
                    type="text"
                    value={startInput}
                    onChange={(e) => handleInputChange(e, true)}
                    onFocus={() => handleInputFocus(true)}
                    onBlur={() => handleInputBlur(true)}
                    className="mb-2 input-default w-full max-w-full"
                    pattern="\d{4}-\d{2}-\d{2}"
                    placeholder="Start date"
                />
            </div>
            <div className='flex flex-col'>
                <input
                    type="text"
                    value={endInput}
                    onChange={(e) => handleInputChange(e, false)}
                    onFocus={() => handleInputFocus(false)}
                    onBlur={() => handleInputBlur(false)}
                    className="mb-2 input-default w-full max-w-full"
                    pattern="\d{4}-\d{2}-\d{2}"
                    placeholder="End date"
                />
            </div>

            {showCalendar && (
                <div className="absolute z-50 mt-1 p-4 rounded-lg bg-white border shadow-lg">
                    <div className="flex items-center justify-between mb-2">
                        <button onClick={handlePrevMonth} className="p-1">
                            <FontAwesomeIcon icon={faCircleLeft} />
                        </button>
                        <div className="text-center text-sm font-normal">{format(currentDate, 'MMMM yyyy')}</div>
                        <button onClick={handleNextMonth} className="p-1">
                            <FontAwesomeIcon icon={faCircleRight} />
                        </button>
                    </div>
                    {generateCalendar()}
                </div>
            )}
        </div>
    );
};

export default DatePicker;
